/* eslint-disable max-len */
import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef } from 'react';
import { XumoButton as Button } from 'ui';

export const VideoPlayer: React.FC<{
  videoSrc: string;
  onClose: () => void;
}> = ({ videoSrc, onClose }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoSrc) return;

    let hls: Hls | null = null;

    const tryNative = () => {
      videoElement.src = videoSrc;
      const onReady = () => {
        videoElement.play().catch(() => {});
      };
      videoElement.addEventListener('loadedmetadata', onReady, { once: true });
      return () => videoElement.removeEventListener('loadedmetadata', onReady);
    };

    // Prefer native HLS first (Safari), then hls.js (Chrome/Firefox/Edge)
    let cleanup: (() => void) | undefined;

    if (videoSrc.endsWith('.m3u8') && videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      cleanup = tryNative();
    } else if (videoSrc.endsWith('.m3u8')) {
      import('hls.js').then(({ default: Hls }) => {
        if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(videoSrc);
          hls.attachMedia(videoElement);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            videoElement.play().catch(() => {});
          });
        } else {
          cleanup = tryNative();
        }
      });
    } else {
      // MP4 or other
      cleanup = tryNative();
    }

    return () => {
      cleanup?.();
      if (hls) {
        hls.destroy();
        hls = null;
      }
    };
  }, [videoSrc]);

  return (
    <AnimatePresence>
      {videoSrc && (
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1, transition: { restDelta: 0.001 } }}
          exit={{ opacity: 0, scale: 1.1 }}
          className="align-center fixed bottom-0 left-0 right-0 top-8 z-50 flex h-screen flex-col items-center justify-center overflow-hidden bg-[#000000ee]"
        >
          <div className="container relative flex aspect-video max-h-[75vh] grow-0 flex-col justify-center lg:w-max">
            <video ref={videoRef} controls autoPlay playsInline width={1920} height={1080} className="aspect-video h-full grow-0 object-contain" />
            <div className="my-4 flex items-center justify-center">
              <Button label="Close video" onClick={onClose} light />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
